/**
 * Warms ProductManager's section cache ahead of a variant change, so the change paints from cache.
 * Intent (hover, touch, focus) warms the option under the pointer. Once the page is idle with the picker on
 * screen, the variants one option change away are warmed too, capped lower on touch devices where data costs more.
 */
class VariantPrefetcher {
	static #DESKTOP_IDLE_PREFETCH_LIMIT = 12;
	static #FINE_POINTER_MEDIA_QUERY = '(hover: hover) and (pointer: fine)';
	static #HOVER_INTENT_DELAY_MS = 65;
	static #IDLE_FALLBACK_DELAY_MS = 1000;
	static #OPTION_CONTAINER_SELECTOR = '.variant-option__container';
	static #OPTION_RADIO_SELECTOR = '.variant-option__radio';
	static #POINTER_TYPE_MOUSE = 'mouse';
	static #TOUCH_IDLE_PREFETCH_LIMIT = 2;

	static #eventStrings() {
		return window.UFT?.EventManager.eventStrings;
	}

	// Data Saver opts out of every download made on a guess, intent and idle alike; the click still fetches normally.
	// Only Chromium reports it; elsewhere the device cap alone bounds the idle cost.
	static #dataSaverOn() {
		return navigator.connection?.saveData === true;
	}

	// A pivot picker's fallback can resolve changes at two options to one variant. Keeping each id's first, likeliest
	// entry lets a capped list reach another variant instead of a repeat.
	static #uniqueById(variants) {
		const seenIds = new Set();
		return variants.filter((variant) => {
			if (seenIds.has(variant.id)) {
				return false;
			}
			seenIds.add(variant.id);
			return true;
		});
	}

	// A sold-out neighbour is rarely picked, so on touch's two slots it would leave the next buyable one cold.
	static #byLikelihood(a, b) {
		const byAvailability = Number(b.variant.available) - Number(a.variant.available);
		if (byAvailability !== 0) {
			return byAvailability;
		}
		if (a.rank !== b.rank) {
			return a.rank - b.rank;
		}
		return a.optionIndex - b.optionIndex;
	}

	// The next value sorts before the previous one at each distance from the selected value.
	static #proximityRank(offset) {
		return offset > 0 ? offset * 2 - 1 : -offset * 2;
	}

	#boundHtmlUpdated;
	#boundSectionCacheCleared;
	#boundViewportChange;
	#connected = false;
	#currentVariantId = null;
	#hoverContainer = null;
	#hoverTimer = null;
	#idlePrefetchStarted = false;
	#picker;
	#pickerListeners;
	#previousVariantId = null;
	#unsubscribeFromProductEvents = [];
	#viewportObserver = null;

	constructor(picker) {
		this.#picker = picker;
		this.#boundHtmlUpdated = this.#handleHtmlUpdated.bind(this);
		// One table for connect() and disconnect(), so an added listener can't be left out of the removal.
		this.#pickerListeners = [
			['pointerover', this.#handlePointerOver.bind(this)],
			['pointerout', this.#handlePointerOut.bind(this)],
			['pointerdown', this.#handlePointerDown.bind(this)],
			['focusin', this.#handleFocusIn.bind(this)],
		];
		this.#boundSectionCacheCleared = this.#handleSectionCacheCleared.bind(this);
		this.#boundViewportChange = this.#handleViewportChange.bind(this);
	}

	connect() {
		const events = VariantPrefetcher.#eventStrings();
		if (!events) {
			console.warn('[VariantPrefetcher] UFT EventManager not found');
			return;
		}
		this.#connected = true;
		this.#currentVariantId ??= Number(this.#picker.findVariantByOptions(this.#picker.readOptions())?.id) || null;
		// Delegated on the picker because each HTML update replaces the option markup.
		this.#pickerListeners.forEach(([eventName, listener]) => this.#picker.addEventListener(eventName, listener));
		this.#unsubscribeFromProductEvents = [
			window.UFT.EventManager.on(events.PRODUCT_HTML_UPDATED, this.#boundHtmlUpdated),
			window.UFT.EventManager.on(events.PRODUCT_SECTION_CACHE_CLEARED, this.#boundSectionCacheCleared),
		];
		// Once idle prefetch has started, HTML updates keep it planned, so a reconnect needs no second start.
		if (this.#idlePrefetchStarted) {
			return;
		}
		this.#viewportObserver = new IntersectionObserver(this.#boundViewportChange);
		this.#viewportObserver.observe(this.#picker);
	}

	disconnect() {
		this.#connected = false;
		this.#pickerListeners.forEach(([eventName, listener]) => this.#picker.removeEventListener(eventName, listener));
		this.#unsubscribeFromProductEvents.forEach((unsubscribe) => unsubscribe());
		this.#unsubscribeFromProductEvents = [];
		this.#viewportObserver?.disconnect();
		this.#viewportObserver = null;
		this.#cancelHoverIntent();
	}

	#handlePointerOver(event) {
		// Touch fires pointerover just before pointerdown, which already announces the intent.
		if (event.pointerType !== VariantPrefetcher.#POINTER_TYPE_MOUSE) {
			return;
		}
		const container = event.target.closest(VariantPrefetcher.#OPTION_CONTAINER_SELECTOR);
		if (!container || container === this.#hoverContainer) {
			return;
		}
		this.#cancelHoverIntent();
		this.#hoverContainer = container;
		this.#hoverTimer = setTimeout(() => this.#announceIntent(container), VariantPrefetcher.#HOVER_INTENT_DELAY_MS);
	}

	#handlePointerOut(event) {
		if (!this.#hoverContainer || this.#hoverContainer.contains(event.relatedTarget)) {
			return;
		}
		this.#cancelHoverIntent();
	}

	#handlePointerDown(event) {
		this.#announceIntent(event.target.closest(VariantPrefetcher.#OPTION_CONTAINER_SELECTOR));
	}

	#handleFocusIn(event) {
		this.#announceIntent(event.target.closest(VariantPrefetcher.#OPTION_CONTAINER_SELECTOR));
	}

	// The observer comes from the callback: an entry delivered after disconnect() would find the field already null.
	#handleViewportChange(entries, observer) {
		if (!entries.some((entry) => entry.isIntersecting)) {
			return;
		}
		observer.disconnect();
		if (this.#viewportObserver === observer) {
			this.#viewportObserver = null;
		}
		this.#idlePrefetchStarted = true;
		this.#whenPageIdle(() => this.#planIdlePrefetch());
	}

	// A variant change moves the set one change away, and a plan change re-keys every section URL, so both re-plan.
	#handleHtmlUpdated({ variantId }) {
		// The option markup was just replaced, so a pending hover points at a detached node.
		this.#cancelHoverIntent();
		this.#trackVariant(variantId);
		this.#replanIdlePrefetch();
	}

	// ProductManager's variant, not the picker's change event: bundle components announce their own variants on that bus.
	#trackVariant(variantId) {
		if (variantId == null || Number(variantId) === this.#currentVariantId) {
			return;
		}
		this.#previousVariantId = this.#currentVariantId;
		this.#currentVariantId = Number(variantId);
	}

	// A cart change can drop the warmed neighbours without repainting, so no HTML update would re-plan them.
	#handleSectionCacheCleared() {
		this.#replanIdlePrefetch();
	}

	#replanIdlePrefetch() {
		if (!this.#idlePrefetchStarted) {
			return;
		}
		this.#whenPageIdle(() => this.#planIdlePrefetch());
	}

	// Repeats aren't deduped here: the renderer already serves a fresh entry or joins the request in flight, and
	// refetches one that failed or expired, which a dedupe here would block until the next HTML update.
	#announceIntent(container) {
		if (VariantPrefetcher.#dataSaverOn()) {
			return;
		}
		const variant = this.#variantForOption(container);
		if (!variant) {
			return;
		}
		window.UFT.EventManager.dispatch(VariantPrefetcher.#eventStrings().PRODUCT_VARIANT_INTENT, { id: variant.id });
	}

	#variantForOption(container) {
		const radio = container?.querySelector(VariantPrefetcher.#OPTION_RADIO_SELECTOR);
		if (!radio) {
			return null;
		}
		const options = this.#picker.readOptions();
		const position = Number(radio.dataset.position);
		if (options[position - 1] === radio.value) {
			return null;
		}
		options[position - 1] = radio.value;
		return this.#picker.resolveVariant({ options, position }) ?? null;
	}

	#cancelHoverIntent() {
		clearTimeout(this.#hoverTimer);
		this.#hoverTimer = null;
		this.#hoverContainer = null;
	}

	// requestIdleCallback isn't Baseline, hence the timeout fallback.
	#whenPageIdle(callback) {
		const scheduleIdle = () => {
			if ('requestIdleCallback' in window) {
				window.requestIdleCallback(callback);
				return;
			}
			setTimeout(callback, VariantPrefetcher.#IDLE_FALLBACK_DELAY_MS);
		};
		if (document.readyState === 'complete') {
			scheduleIdle();
			return;
		}
		window.addEventListener('load', scheduleIdle, { once: true });
	}

	#planIdlePrefetch() {
		// A callback scheduled before disconnect() can still land; a detached picker must not prefetch.
		if (!this.#connected) {
			return;
		}
		if (VariantPrefetcher.#dataSaverOn()) {
			return;
		}
		const variants = this.#variantsOneChangeAway().slice(0, this.#idlePrefetchLimit());
		if (variants.length === 0) {
			return;
		}
		window.UFT.EventManager.dispatch(VariantPrefetcher.#eventStrings().PRODUCT_VARIANTS_PREFETCH, {
			variants: variants.map((variant) => ({ id: variant.id })),
		});
	}

	#idlePrefetchLimit() {
		if (window.matchMedia(VariantPrefetcher.#FINE_POINTER_MEDIA_QUERY).matches) {
			return VariantPrefetcher.#DESKTOP_IDLE_PREFETCH_LIMIT;
		}
		return VariantPrefetcher.#TOUCH_IDLE_PREFETCH_LIMIT;
	}

	// Buyable first, then nearest, so a capped list keeps the values a shopper is likeliest to pick next.
	#variantsOneChangeAway() {
		const selected = this.#picker.readOptions();
		const variants = this.#picker
			.optionValueOrder()
			.flatMap((values, optionIndex) => this.#candidatesForOption({ selected, values, optionIndex }))
			.sort(VariantPrefetcher.#byLikelihood)
			.map((candidate) => candidate.variant);
		return this.#previousVariantFirst(VariantPrefetcher.#uniqueById(variants));
	}

	// Comparing two flavours means going back to the one just left, and ranking alone can leave it out of touch's two slots.
	#previousVariantFirst(variants) {
		const previous = variants.find((variant) => Number(variant.id) === this.#previousVariantId);
		if (!previous) {
			return variants;
		}
		return [previous, ...variants.filter((variant) => variant !== previous)];
	}

	// optionIndex counts from 0, as the options array does; resolveVariant's position counts from 1, as data-position does.
	#candidatesForOption({ selected, values, optionIndex }) {
		const selectedIndex = values.indexOf(selected[optionIndex]);
		return values.flatMap((value, valueIndex) => {
			if (valueIndex === selectedIndex) {
				return [];
			}
			const options = [...selected];
			options[optionIndex] = value;
			const variant = this.#picker.resolveVariant({ options, position: optionIndex + 1 });
			if (!variant) {
				return [];
			}
			return [{ variant, optionIndex, rank: VariantPrefetcher.#proximityRank(valueIndex - selectedIndex) }];
		});
	}
}
